import React, { useEffect, useId, useState } from 'react';
import { Search } from 'lucide-react';
import { carrierLabel } from '../../constants/shipments';
import { shipmentsApi } from '../../utils/shipmentsApi';
import { StatusBadge } from './StatusBadge';
import { useRemote } from './useRemote';

const MIN_CHARS = 2;
const DEBOUNCE_MS = 300;

// Finds a shipment through GET /?q= (all tabs) and lets the user choose one (radio list).
// Used by "Sloučit" (merge target) and by "Přiřadit k zásilce" in the review queue.
const ShipmentSearchPicker = ({ excludeId, value, onChange }) => {
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  const name = useId();
  const inputId = useId();
  const res = useRemote(q.length >= MIN_CHARS ? `q:${q}` : null, () => shipmentsApi.list({ tab: 'all', q, limit: 10 }));

  useEffect(() => {
    const timer = setTimeout(() => setQ(text.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [text]);

  const items = (res.data?.items || []).filter((i) => i.id !== excludeId);
  const ready = q.length >= MIN_CHARS;

  return (
    <div className="shp-stack" style={{ gap: 10 }}>
      <div className="shp-field">
        <label htmlFor={inputId} className="shp-label">Najít zásilku</label>
        <div style={{ position: 'relative' }}>
          <Search size={18} aria-hidden="true" style={{ position: 'absolute', left: 12, top: 13, color: '#999' }} />
          <input
            id={inputId} type="search" className="shp-input" style={{ paddingLeft: 40 }} value={text} autoComplete="off" maxLength={100}
            placeholder="Číslo zásilky, objednávka, popis…" aria-describedby={`${inputId}-hint`} onChange={(e) => setText(e.target.value)}
          />
        </div>
        <p id={`${inputId}-hint`} className="shp-hint">Zadejte aspoň {MIN_CHARS} znaky.</p>
      </div>
      <div aria-live="polite">
        {res.error && <p role="alert" className="shp-error">{res.error.message}</p>}
        {ready && !res.error && res.loaded && !res.loading && items.length === 0 && <p className="shp-muted shp-small">Nic nenalezeno.</p>}
      </div>
      {items.length > 0 && (
        <fieldset style={{ border: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
          <legend className="shp-sr">Nalezené zásilky</legend>
          {items.map((it) => (
            <label key={it.id} className="shp-opt" style={{ border: '1px solid #E0E0E0', alignItems: 'flex-start', background: value?.id === it.id ? '#FFF8F8' : '#FFF' }}>
              <input type="radio" name={name} checked={value?.id === it.id} onChange={() => onChange(it)} style={{ marginTop: 3, accentColor: '#E53935' }} />
              <span style={{ display: 'grid', gap: 2, minWidth: 0 }}>
                <span className="shp-wrap" dir="auto"><strong>{it.trackingNumber || it.orderRef || `Zásilka č. ${it.id}`}</strong> · {carrierLabel(it.carrierCode, it.carrierName)}</span>
                {it.description && <span className="shp-small shp-muted shp-wrap" dir="auto">{it.description}</span>}
                <span><StatusBadge status={it.status} /></span>
              </span>
            </label>
          ))}
        </fieldset>
      )}
    </div>
  );
};

export default ShipmentSearchPicker;
